import { nanoid } from 'nanoid';

export type FeedbackQuestionType = 'text' | 'list' | 'rating' | 'yesno' | 'roles';

const FEEDBACK_TYPE_LABELS: Record<FeedbackQuestionType, string> = {
  text: 'Text',
  list: 'List of Industries',
  rating: '1-5 Rating',
  yesno: 'Yes or No',
  roles: 'List of Roles'
};

const FEEDBACK_TYPE_ANSWER_FORMAT: Record<FeedbackQuestionType, string> = {
  text: 'Open input field',
  list: 'List of industries',
  rating: '1-5 rating',
  yesno: 'Yes, No',
  roles: 'List of roles'
};

export const mapFeedbackTypeToAnswerFormat = (type: FeedbackQuestionType): string =>
  FEEDBACK_TYPE_ANSWER_FORMAT[type] || FEEDBACK_TYPE_ANSWER_FORMAT.text;

export const mapAnswerFormatToFeedbackType = (value?: string): FeedbackQuestionType => {
  const normalized = (value || '').toLowerCase();
  if (normalized.includes('role')) return 'roles';
  if (normalized.includes('rating') || normalized.includes('1-5')) return 'rating';
  if (normalized.includes('yes')) return 'yesno';
  if (normalized.includes('list')) return 'list';
  return 'text';
};

export const getFeedbackTypeLabel = (type: FeedbackQuestionType): string => FEEDBACK_TYPE_LABELS[type] ?? FEEDBACK_TYPE_LABELS.text;

export interface LessonMaterial {
  id: string;
  title: string;
  reference: string;
  thumbnail?: string;
  articleId?: string;
  readTime?: string;
  source?: 'ai' | 'library' | 'custom';
}

export interface FeedbackQuestion {
  id: string;
  _id?: string;
  prompt: string;
  type: FeedbackQuestionType;
}

export interface MultipleChoiceOption {
  id: string;
  label: string;
}

export interface MultipleChoiceQuestion {
  id: string;
  _id?: string;
  prompt: string;
  options: MultipleChoiceOption[];
  correctOptionId?: string;
}

export interface Lesson {
  id: string;
  sourceId?: string;
  title: string;
  description: string;
  materials: LessonMaterial[];
  feedbackQuestions: FeedbackQuestion[];
  multipleChoiceQuestions: MultipleChoiceQuestion[];
  status?: string;
  usedRag?: boolean;
  generatedAt?: string | null;
}

export interface ModuleDraft {
  id: string;
  sourceId?: string;
  title: string;
  objective?: string;
  lessons: Lesson[];
  status?: string;
  completedLessonsCount?: number;
  totalLessonsCount?: number;
}

export const createLesson = (): Lesson => ({
  id: nanoid(),
  title: 'New Lesson',
  description: '',
  materials: [],
  feedbackQuestions: [],
  multipleChoiceQuestions: []
});

export const createModule = (index: number): ModuleDraft => ({
  id: nanoid(),
  title: `Module ${index}`,
  objective: '',
  lessons: [createLesson()]
});
